import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ButtonBase,
} from '@mui/material';
import {
  ReceiptLong as ReceiptIcon,
  Add as AddIcon,
  CheckCircle as PaidIcon,
  Delete as DeleteIcon,
  Autorenew as AutorenewIcon,
  WarningAmber as WarningIcon,
  CalendarMonth as CalendarMonthIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  UploadFile as UploadFileIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import {
  getBills,
  getBillsSummary,
  createBill,
  updateBill,
  deleteBill as deleteBillApi,
  markBillPaid,
  getRecurringStreamReviewCandidates,
  approveRecurringStream,
  rejectRecurringStream,
  parseBill,
  getBillsCalendar,
} from '../services/api';
import { colors } from '../styles';

const STATUS_COLORS = {
  upcoming: '#4CAF50',
  due_soon: '#FF9800',
  due_today: '#FF9800',
  past_due: '#F44336',
  paid: '#9E9E9E',
};

const STATUS_LABELS = {
  upcoming: 'Upcoming',
  due_soon: 'Due Soon',
  due_today: 'Due Today',
  past_due: 'Past Due',
  paid: 'Paid',
};

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi_annually', label: 'Semi-Annually' },
  { value: 'annually', label: 'Annually' },
];

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 14,
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  transition: 'box-shadow 0.2s',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
}));

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** Max length of base64 payload for a 5MB raw image (~6.99M characters). */
const MAX_BILL_IMAGE_BASE64_CHAR_COUNT = Math.ceil((5 * 1024 * 1024 / 3) * 4);

export default function Bills() {
  useAuth();
  const { showError } = useError();

  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('unpaid');
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reviewCandidates, setReviewCandidates] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(null);

  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [formData, setFormData] = useState({
    payee: '',
    amountCents: '',
    dueDate: '',
    billType: 'one_time',
    recurringFrequency: 'monthly',
    autopay: false,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const billsCountRef = useRef(0);
  const billFileInputRef = useRef(null);

  const [viewMode, setViewMode] = useState('list');
  const nowInit = new Date();
  const [calYear, setCalYear] = useState(nowInit.getUTCFullYear());
  const [calMonth, setCalMonth] = useState(nowInit.getUTCMonth());
  const [calendarDays, setCalendarDays] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [parseUploading, setParseUploading] = useState(false);
  const [uploadPrefill, setUploadPrefill] = useState(null);

  const fetchData = useCallback(async (loadMore = false) => {
    try {
      const params = { limit: 50, skip: loadMore ? billsCountRef.current : 0 };
      if (filter === 'unpaid') {
        params.status = 'upcoming,due_soon,due_today,past_due';
      } else if (filter !== 'all') {
        if (filter !== 'due_soon') {
          params.status = filter;
        }
      }

      if (loadMore) {
        setLoadingMore(true);
        const billsRes = await getBills(params);
        let newBills = billsRes.data || [];
        if (filter === 'due_soon') {
          newBills = newBills.filter(b => b.status === 'due_soon' || b.status === 'due_today');
        } else if (filter === 'unpaid') {
          newBills = newBills.filter(b => b.status !== 'paid');
        }
        setBills(prev => {
          const updated = [...prev, ...newBills];
          billsCountRef.current = updated.length;
          return updated;
        });
        setHasMore(billsRes.pagination?.hasMore || false);
      } else {
        const [billsRes, summaryRes, reviewRes] = await Promise.all([
          getBills(params),
          getBillsSummary(),
          getRecurringStreamReviewCandidates().catch(() => ({ streams: [] })),
        ]);

        let fetchedBills = billsRes.data || [];
        if (filter === 'due_soon') {
          fetchedBills = fetchedBills.filter(b => b.status === 'due_soon' || b.status === 'due_today');
        } else if (filter === 'unpaid') {
          fetchedBills = fetchedBills.filter(b => b.status !== 'paid');
        }
        setBills(fetchedBills);
        billsCountRef.current = fetchedBills.length;
        setHasMore(billsRes.pagination?.hasMore || false);
        setSummary(summaryRes.data || null);
        setReviewCandidates(reviewRes.streams || []);
      }
    } catch (err) {
      showError('Failed to load bills');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filter, showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadCalendar = useCallback(async () => {
    setCalendarLoading(true);
    try {
      const res = await getBillsCalendar(calYear, calMonth + 1);
      const payload = res?.data ?? res;
      const inner = payload?.data ?? payload;
      setCalendarDays(inner?.days || {});
    } catch {
      showError('Failed to load calendar');
      setCalendarDays({});
    } finally {
      setCalendarLoading(false);
    }
  }, [calYear, calMonth, showError]);

  useEffect(() => {
    if (viewMode === 'calendar') {
      loadCalendar();
    }
  }, [viewMode, loadCalendar]);

  const handleOpenCreate = () => {
    setEditingBill(null);
    setUploadPrefill(null);
    setFormData({ payee: '', amountCents: '', dueDate: '', billType: 'one_time', recurringFrequency: 'monthly', autopay: false, notes: '' });
    setDialogOpen(true);
  };

  const handleBillImageSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpeg)$/i.test(file.type)) {
      showError('Please upload a PNG or JPEG image.');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string') return;
      const base64Payload = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      if (base64Payload.length > MAX_BILL_IMAGE_BASE64_CHAR_COUNT) {
        showError('Image is too large (max 5MB).');
        return;
      }
      setParseUploading(true);
      try {
        const res = await parseBill(dataUrl);
        const payload = res?.data ?? res;
        const d = payload?.data ?? payload;
        if (!d || !d.payee) {
          showError('Could not read this bill. Try a clearer photo or enter details manually.');
          return;
        }
        const dueRaw = d.dueDate ? new Date(d.dueDate) : null;
        const dueDate = dueRaw && !Number.isNaN(dueRaw.getTime())
          ? dueRaw.toISOString().split('T')[0]
          : '';
        const isRecurring = d.billType === 'recurring' || d.recurringFrequency;
        setEditingBill(null);
        setFormData({
          payee: d.payee || '',
          amountCents: String((d.amountCents != null ? d.amountCents : 0) / 100),
          dueDate,
          billType: isRecurring ? 'recurring' : 'one_time',
          recurringFrequency: d.recurringFrequency || 'monthly',
          autopay: !!d.autopay,
          notes: d.notes || '',
        });
        setUploadPrefill({
          scannedImageBase64: base64Payload,
          aiParsedData: d,
        });
        setDialogOpen(true);
      } catch (err) {
        const msg = err?.response?.data?.error;
        showError(typeof msg === 'string' ? msg : 'Failed to parse bill');
      } finally {
        setParseUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleUploadBillClick = () => {
    billFileInputRef.current?.click();
  };

  const goPrevMonth = () => {
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear((y) => y - 1);
    } else {
      setCalMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear((y) => y + 1);
    } else {
      setCalMonth((m) => m + 1);
    }
  };

  const handleOpenEdit = (bill) => {
    setUploadPrefill(null);
    setEditingBill(bill);
    setFormData({
      payee: bill.payee || '',
      amountCents: String((bill.amountCents || 0) / 100),
      dueDate: bill.dueDate ? bill.dueDate.split('T')[0] : '',
      billType: bill.billType || 'one_time',
      recurringFrequency: bill.recurringFrequency || 'monthly',
      autopay: bill.autopay || false,
      notes: bill.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const amountCents = Math.round(parseFloat(formData.amountCents) * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      showError('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        payee: formData.payee,
        amountCents,
        dueDate: formData.dueDate,
        billType: formData.billType,
        recurringFrequency: formData.billType === 'recurring' ? formData.recurringFrequency : null,
        autopay: formData.autopay,
        notes: formData.notes || null,
      };

      if (!editingBill && uploadPrefill) {
        payload.scannedImageBase64 = uploadPrefill.scannedImageBase64;
        payload.aiParsedData = uploadPrefill.aiParsedData;
      }

      if (editingBill) {
        await updateBill(editingBill._id, payload);
      } else {
        await createBill(payload);
      }

      setDialogOpen(false);
      setUploadPrefill(null);
      fetchData();
      if (viewMode === 'calendar') {
        loadCalendar();
      }
    } catch (err) {
      showError(editingBill ? 'Failed to update bill' : 'Failed to create bill');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bill) => {
    if (!window.confirm(`Delete bill "${bill.payee}"?`)) return;
    try {
      await deleteBillApi(bill._id);
      fetchData();
    } catch (err) {
      showError('Failed to delete bill');
    }
  };

  const handleMarkPaid = async (bill) => {
    try {
      await markBillPaid(bill._id);
      fetchData();
    } catch (err) {
      showError('Failed to mark bill as paid');
    }
  };

  const handleApproveStream = async (stream) => {
    setReviewLoading(stream._id);
    try {
      await approveRecurringStream(stream._id);
      setReviewCandidates(prev => prev.filter(s => s._id !== stream._id));
      fetchData();
    } catch (err) {
      showError('Failed to approve recurring stream');
    } finally {
      setReviewLoading(null);
    }
  };

  const handleRejectStream = async (stream) => {
    setReviewLoading(stream._id);
    try {
      await rejectRecurringStream(stream._id);
      setReviewCandidates(prev => prev.filter(s => s._id !== stream._id));
    } catch (err) {
      showError('Failed to reject recurring stream');
    } finally {
      setReviewLoading(null);
    }
  };

  const summaryText = summary
    ? `${summary.totalUnpaid?.count || 0} unpaid · ${formatCurrency((summary.totalUnpaid?.totalCents || 0) / 100)}`
    : '';

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header */}
        <input
          ref={billFileInputRef}
          type="file"
          accept="image/jpeg,image/png"
          style={{ display: 'none' }}
          onChange={handleBillImageSelected}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Bills</Typography>
            {summaryText && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {summaryText}
                {summary?.pastDue?.count > 0 && (
                  <Typography component="span" sx={{ color: '#F44336', fontWeight: 600, ml: 1 }}>
                    · {summary.pastDue.count} past due
                  </Typography>
                )}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              size="small"
              onChange={(_, v) => v && setViewMode(v)}
            >
              <ToggleButton value="list" sx={{ textTransform: 'none' }}>
                <ViewListIcon sx={{ fontSize: 18, mr: 0.5 }} />
                List
              </ToggleButton>
              <ToggleButton value="calendar" sx={{ textTransform: 'none' }}>
                <CalendarMonthIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Calendar
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={parseUploading ? <CircularProgress size={18} /> : <UploadFileIcon />}
              onClick={handleUploadBillClick}
              disabled={parseUploading}
              sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}
            >
              Upload bill
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}
              sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600, bgcolor: colors.primary || '#FF5D82' }}>
              Add Bill
            </Button>
          </Box>
        </Box>

        {/* Recurring Stream Review Section */}
        {reviewCandidates.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <AutorenewIcon sx={{ color: colors.primary || '#FF5D82', fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Recurring Detected ({reviewCandidates.length})
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {reviewCandidates.map((stream) => {
                const ai = stream.aiClassification || {};
                const payee = ai.normalizedPayee || stream.merchantName || stream.description || 'Unknown';
                const amount = (stream.lastAmountCents || stream.averageAmountCents || 0) / 100;
                const isProcessing = reviewLoading === stream._id;
                return (
                  <Grid item xs={12} sm={6} md={4} key={stream._id}>
                    <StyledCard>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <AutorenewIcon fontSize="small" sx={{ color: colors.primary || '#FF5D82' }} />
                          <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1 }}>
                            {payee}
                          </Typography>
                          <Chip label="Plaid" size="small" variant="outlined"
                            sx={{ fontSize: 10, height: 20, borderColor: colors.primary || '#FF5D82', color: colors.primary || '#FF5D82' }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {stream.plaidFrequency?.toLowerCase()} · {formatCurrency(amount)}
                          {ai.confidence ? ` · ${Math.round(ai.confidence * 100)}%` : ''}
                        </Typography>
                        {ai.explanation && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {ai.explanation}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                          <Button size="small" variant="outlined" color="error"
                            disabled={isProcessing} onClick={() => handleRejectStream(stream)}
                            sx={{ borderRadius: 3, textTransform: 'none', fontSize: 13 }}>
                            {isProcessing ? <CircularProgress size={16} /> : 'Not a Bill'}
                          </Button>
                          <Button size="small" variant="contained"
                            disabled={isProcessing} onClick={() => handleApproveStream(stream)}
                            sx={{ borderRadius: 3, textTransform: 'none', fontSize: 13, bgcolor: colors.primary || '#FF5D82' }}>
                            {isProcessing ? <CircularProgress size={16} color="inherit" /> : 'Add as Bill'}
                          </Button>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* Filter Chips (list view) */}
        {viewMode === 'list' && (
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup value={filter} exclusive onChange={(_, v) => v && setFilter(v)} size="small">
              <ToggleButton value="unpaid" sx={{ textTransform: 'none' }}>Unpaid</ToggleButton>
              <ToggleButton value="all" sx={{ textTransform: 'none' }}>All</ToggleButton>
              <ToggleButton value="due_soon" sx={{ textTransform: 'none' }}>Due Soon</ToggleButton>
              <ToggleButton value="past_due" sx={{ textTransform: 'none' }}>Past Due</ToggleButton>
              <ToggleButton value="paid" sx={{ textTransform: 'none' }}>Paid</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Calendar view */}
        {viewMode === 'calendar' && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <IconButton onClick={goPrevMonth} aria-label="Previous month" size="small">
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="subtitle1" fontWeight={600}>
                {new Date(Date.UTC(calYear, calMonth, 1)).toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                  timeZone: 'UTC',
                })}
              </Typography>
              <IconButton onClick={goNextMonth} aria-label="Next month" size="small">
                <ChevronRightIcon />
              </IconButton>
            </Box>
            {calendarLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={1}>
                {Array.from(
                  { length: new Date(Date.UTC(calYear, calMonth + 1, 0)).getUTCDate() },
                  (_, i) => i + 1
                ).map((day) => {
                  const dayBills = calendarDays[day] || [];
                  return (
                    <Grid item xs={6} sm={4} md={3} key={day}>
                      <StyledCard sx={{ minHeight: 100 }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            {day}
                          </Typography>
                          {dayBills.length === 0 ? (
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                              —
                            </Typography>
                          ) : (
                            dayBills.map((b) => (
                              <ButtonBase
                                key={b._id}
                                focusRipple
                                onClick={() => handleOpenEdit(b)}
                                sx={{
                                  display: 'block',
                                  mt: 0.5,
                                  width: '100%',
                                  textAlign: 'left',
                                  justifyContent: 'flex-start',
                                  borderRadius: 0.5,
                                }}
                                aria-label={`Edit bill ${b.payee || 'Bill'} for ${formatCurrency((b.amountCents || 0) / 100)}`}
                              >
                                <Typography variant="caption" component="span" noWrap sx={{ display: 'block' }}>
                                  {b.payee} · {formatCurrency((b.amountCents || 0) / 100)}
                                </Typography>
                              </ButtonBase>
                            ))
                          )}
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

        {/* Bills List */}
        {viewMode === 'list' && loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : viewMode === 'list' && bills.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No bills yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Add a bill manually or upload a photo of a bill.
            </Typography>
          </Box>
        ) : viewMode === 'list' ? (
          <Grid container spacing={2}>
            {bills.map((bill) => {
              const statusColor = STATUS_COLORS[bill.status] || '#9E9E9E';
              return (
                <Grid item xs={12} key={bill._id}>
                  <StyledCard sx={{ cursor: 'pointer' }} onClick={() => handleOpenEdit(bill)}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${statusColor}15` }}>
                        <ReceiptIcon sx={{ color: statusColor, fontSize: 20 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {bill.payee}
                          {bill.plaidStreamId && (
                            <Chip label="Plaid" size="small" variant="outlined"
                              sx={{ ml: 1, fontSize: 10, height: 18, borderColor: colors.primary || '#FF5D82', color: colors.primary || '#FF5D82' }} />
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Due {formatDate(bill.dueDate)}
                          {bill.autopay ? ' · Autopay' : ''}
                          {bill.billType === 'recurring' ? ' · Recurring' : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {formatCurrency(bill.amountCents / 100)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          <Chip label={STATUS_LABELS[bill.status] || bill.status} size="small"
                            sx={{ bgcolor: `${statusColor}20`, color: statusColor, fontWeight: 600, fontSize: 11 }} />
                          {!bill.budgetCategoryId && bill.status !== 'paid' && (
                            <Tooltip title="This bill is not linked to a budget category. Assign it to keep spending on-budget.">
                              <Chip
                                icon={<WarningIcon sx={{ fontSize: 14 }} />}
                                label="No Budget"
                                size="small"
                                sx={{ bgcolor: '#FF980018', color: '#FF9800', fontWeight: 600, fontSize: 10, height: 22 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {bill.status !== 'paid' && (
                          <Tooltip title="Mark Paid">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMarkPaid(bill); }}
                              sx={{ color: '#4CAF50' }}>
                              <PaidIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(bill); }}
                            sx={{ color: '#F44336' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              );
            })}
            {hasMore && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => fetchData(true)}
                    disabled={loadingMore}
                    sx={{ textTransform: 'none' }}
                  >
                    {loadingMore ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                    Load More
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        ) : null}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setUploadPrefill(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>{editingBill ? 'Edit Bill' : 'Add Bill'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField label="Payee" fullWidth value={formData.payee}
                onChange={(e) => setFormData(prev => ({ ...prev, payee: e.target.value }))} required />
              <TextField label="Amount ($)" fullWidth type="number" value={formData.amountCents}
                onChange={(e) => setFormData(prev => ({ ...prev, amountCents: e.target.value }))}
                inputProps={{ step: '0.01', min: '0.01' }} required />
              <TextField label="Due Date" fullWidth type="date" value={formData.dueDate}
                onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} required />
              <ToggleButtonGroup value={formData.billType} exclusive size="small"
                onChange={(_, v) => v && setFormData(prev => ({ ...prev, billType: v }))}>
                <ToggleButton value="one_time" sx={{ textTransform: 'none' }}>One-time</ToggleButton>
                <ToggleButton value="recurring" sx={{ textTransform: 'none' }}>Recurring</ToggleButton>
              </ToggleButtonGroup>
              {formData.billType === 'recurring' && (
                <FormControl fullWidth size="small">
                  <InputLabel>Frequency</InputLabel>
                  <Select value={formData.recurringFrequency} label="Frequency"
                    onChange={(e) => setFormData(prev => ({ ...prev, recurringFrequency: e.target.value }))}>
                    {FREQUENCY_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField label="Notes" fullWidth multiline rows={2} value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving || !formData.payee || !formData.amountCents || !formData.dueDate}
              sx={{ textTransform: 'none', bgcolor: colors.primary || '#FF5D82' }}>
              {saving ? <CircularProgress size={20} /> : (editingBill ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
}
